/**
 * Category Utilities
 * Reusable functions for mapping and transforming category data
 */

import type { CategoryInfo } from '@/types/api/vendor';
import type { CategoryListItem } from '@/types/api/category';
import type { CategoryLevelConfig, ProductCategory } from '@/types/api/productCategory';
// Static brand-purple fallback for non-themed (pure-function) contexts. Single
// source of truth in categoryColorPresets; components with a live theme prefer
// token.colorPrimary (light/dark aware) instead of this constant.
import { DEFAULT_CATEGORY_COLOR } from '@/constants/categoryColorPresets';

/**
 * Category color map type - maps categoryId or category name to color
 */
export type CategoryColorMap = Map<number | string, string>;

/**
 * Build a color map from category list for efficient lookups
 * @param categories - Array of categories with colors
 * @returns Map with categoryId and category name as keys, color as value
 */
export const buildCategoryColorMap = (
  categories: CategoryListItem[]
): CategoryColorMap => {
  const colorMap = new Map<number | string, string>();

  categories.forEach((cat) => {
    // Map by categoryId (number)
    colorMap.set(cat.categoryId, cat.color || DEFAULT_CATEGORY_COLOR);
    // Also map by category name (string) for fallback matching
    colorMap.set(cat.category.toLowerCase(), cat.color || DEFAULT_CATEGORY_COLOR);
  });

  return colorMap;
};

/**
 * Get color for a category from the color map
 * @param colorMap - Pre-built category color map
 * @param categoryId - Category ID (number)
 * @param categoryName - Category name (string, optional fallback)
 * @returns Color hex code
 */
export const getCategoryColor = (
  colorMap: CategoryColorMap,
  categoryId?: number,
  categoryName?: string
): string => {
  // Try by ID first
  if (categoryId !== undefined && colorMap.has(categoryId)) {
    return colorMap.get(categoryId)!;
  }

  // Try by name (case-insensitive)
  if (categoryName && colorMap.has(categoryName.toLowerCase())) {
    return colorMap.get(categoryName.toLowerCase())!;
  }

  return DEFAULT_CATEGORY_COLOR;
};

/**
 * Normalize vendor categories to CategoryInfo[] with colors
 * Handles both CategoryInfo[] and string[] from backend
 *
 * @param categories - Raw categories from vendor API (CategoryInfo[] | string[])
 * @param colorMap - Pre-built category color map from category list
 * @returns Normalized CategoryInfo[] with colors
 */
export const normalizeVendorCategories = (
  categories: CategoryInfo[] | string[] | undefined | null,
  colorMap: CategoryColorMap
): CategoryInfo[] => {
  if (!categories || categories.length === 0) {
    return [];
  }

  return categories.map((cat, index) => {
    if (typeof cat === 'string') {
      // Backend returned string[] - find color from map
      return {
        categoryId: index,
        category: cat,
        color: getCategoryColor(colorMap, undefined, cat),
      };
    }

    // Backend returned CategoryInfo[] - ensure color is present
    return {
      ...cat,
      color: cat.color || getCategoryColor(colorMap, cat.categoryId, cat.category),
    };
  });
};

/**
 * Enrich a single CategoryInfo with color from map
 * @param category - Category info (may or may not have color)
 * @param colorMap - Pre-built category color map
 * @returns CategoryInfo with color guaranteed
 */
export const enrichCategoryWithColor = (
  category: CategoryInfo | string,
  colorMap: CategoryColorMap
): CategoryInfo => {
  if (typeof category === 'string') {
    return {
      categoryId: 0,
      category: category,
      color: getCategoryColor(colorMap, undefined, category),
    };
  }

  return {
    ...category,
    color: category.color || getCategoryColor(colorMap, category.categoryId, category.category),
  };
};

/**
 * Find category by ID from list
 * @param categories - Full category list
 * @param categoryId - ID to find
 * @returns CategoryListItem or undefined
 */
export const findCategoryById = (
  categories: CategoryListItem[],
  categoryId: number
): CategoryListItem | undefined => {
  return categories.find((cat) => cat.categoryId === categoryId);
};

/**
 * Find category by name from list (case-insensitive)
 * @param categories - Full category list
 * @param categoryName - Name to find
 * @returns CategoryListItem or undefined
 */
export const findCategoryByName = (
  categories: CategoryListItem[],
  categoryName: string
): CategoryListItem | undefined => {
  const lowerName = categoryName.toLowerCase();
  return categories.find((cat) => cat.category.toLowerCase() === lowerName);
};

// ---------------------------------------------------------------------------
// Product taxonomy (5-level `ProductCategory`) — code suggestion + availability
//
// Pure helpers for the inline quick-create flow (Slice 3.3). They run against
// the FULL node set the tree already loads client-side (`includeInactive: true`,
// useTaxonomy.ts) so uniqueness is checked across active AND inactive codes —
// mirroring the server guard in `mocks/handlers/productCategory.ts`
// (`guardCreate`, code dedupe ~line 85). The server `CODE_TAKEN` (409) stays the
// race backstop. NOTE: these are the 5-level product taxonomy (NOT the flat
// party categories above); they never touch the numeric-`categoryId` model.
// ---------------------------------------------------------------------------

/** First `count` whitespace-separated tokens of `name` (default 2), trimmed. */
const firstTokens = (name: string, count = 2): string =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, count).join(' ');

/** ASCII code slug: each non-alphanumeric run → one dash, edges trimmed, upper-cased. */
const slug = (text: string): string =>
  text
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toUpperCase();

/**
 * Suggest a category code for a new node: `<PARENT_CODE>-<NAME_SLUG>` upper-cased,
 * appending a `-2`/`-3`… numeric suffix until the code is not already used
 * anywhere in `nodes` (case-insensitive, active + inactive — the same dedupe
 * basis as `guardCreate`). An empty `nameEn` yields the parent prefix only
 * (`<PARENT_CODE>-`); no parent + empty name yields `''`.
 *
 * The result is a starting suggestion the user can edit (Shopify
 * suggest-and-confirm); validate the final value with `isCategoryCodeAvailable`.
 */
export const suggestCategoryCode = (
  parent: ProductCategory | null | undefined,
  nameEn: string,
  nodes: ProductCategory[]
): string => {
  const prefix = parent?.code ? `${parent.code}-` : '';
  const base = `${prefix}${slug(firstTokens(nameEn))}`.toUpperCase();

  const isTaken = (candidate: string): boolean =>
    nodes.some((n) => n.code.toLowerCase() === candidate.toLowerCase());

  if (!isTaken(base)) return base;
  let suffix = 2;
  while (isTaken(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
};

/**
 * Client-side code availability — `true` when no node in `nodes` already uses
 * `code` (case-insensitive, trimmed, active + inactive). Parity with
 * `guardCreate`'s duplicate check (productCategory.ts ~line 85) for any non-empty
 * code, so validate-on-blur agrees with the server guard; server `CODE_TAKEN`
 * (409) is the race backstop. Required/empty-field checks are the caller's
 * responsibility (the form validates `code` as required before calling this).
 */
export const isCategoryCodeAvailable = (code: string, nodes: ProductCategory[]): boolean => {
  const target = code.trim().toLowerCase();
  return !nodes.some((n) => n.code.toLowerCase() === target);
};

/**
 * New-selection set for the TaxonomyCascader / search-first picker: active nodes
 * at enabled levels only. Hidden-level read rule — disabled-level nodes stay in
 * breadcrumbs and plain read views; they are excluded ONLY here.
 *
 * This is the ONE shippable predicate for "selectable categories". It was lifted
 * out of `mocks/handlers/productCategory.ts` (Slice 4.1) because `src/` cannot
 * import `mocks/`; the mock handler now imports it back and re-exports it, so the
 * `?forSelection=true` GET, the picker's `selectablePathOptions` (useTaxonomy.ts)
 * and the vitest parity assert all route through this single function — no drift.
 */
export const selectableCategories = (
  nodes: ProductCategory[],
  levels: CategoryLevelConfig[]
): ProductCategory[] => {
  const enabled = new Set(levels.filter((l) => l.enabled).map((l) => l.level));
  return nodes.filter((n) => n.isActive && enabled.has(n.depth));
};
